/**
 * Uma pergunta por vez, e o rascunho que sobrevive à rede.
 *
 * O rascunho mora AQUI, e o componente é montado com `key={no.id}`. As duas
 * coisas juntas dão de graça o comportamento que o convidado precisa: trocar de
 * pergunta limpa o campo (componente novo), e uma falha de envio NÃO limpa
 * nada (mesmo componente, mesmo estado) — que é o caso que mais acontece, no
 * celular, na rua.
 *
 * O que este componente NÃO faz é decidir se a resposta serve. Pergunta
 * obrigatória vazia é recusada pelo banco, com uma mensagem que nomeia a
 * pergunta; repetir essa regra aqui criaria uma segunda cópia dela, e a cópia
 * do front é a que envelhece.
 */

import { useEffect, useRef, useState } from 'react';

import type { NoDoConvite, Valor } from '../../tipos';
import type { Problema } from '../../ganchos/useConvite';
import { Controle } from './Controle';
import { lerLimites, numeroOuTexto, valorInicial } from './valores';

export type PropsDaPergunta = {
  readonly no: NoDoConvite;
  readonly enviando: boolean;
  readonly problema: Problema | null;
  /** Levar o foco ao título da pergunta. Falso na primeira, que já está lida. */
  readonly focar: boolean;
  readonly aoEnviar: (valor: Valor) => void;
};

/** A faixa que o organizador escreveu, dita para quem responde. */
function faixaLegivel(minimo: number | null, maximo: number | null): string | null {
  if (minimo !== null && maximo !== null) return `De ${minimo} a ${maximo}.`;
  if (minimo !== null) return `Mínimo ${minimo}.`;
  if (maximo !== null) return `Máximo ${maximo}.`;
  return null;
}

export function Pergunta({ no, enviando, problema, focar, aoEnviar }: PropsDaPergunta) {
  const [rascunho, definirRascunho] = useState<Valor>(() => valorInicial(no));
  const titulo = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    // Sem animação de troca, o foco é o que diz "mudou de pergunta" — e é o
    // que faz o leitor de tela anunciar a próxima sem o convidado procurar.
    if (focar) titulo.current?.focus();
  }, [focar]);

  const idRotulo = `pergunta-${no.id}`;
  const idAjuda = `ajuda-${no.id}`;
  const idErro = `erro-${no.id}`;

  const limites = lerLimites(no);
  const faixa = no.subtipo === 'numero' ? faixaLegivel(limites.minimo, limites.maximo) : null;
  const ajuda = no.ajuda.trim();
  const temAjuda = ajuda !== '' || faixa !== null;
  const erroNoCampo = problema?.noCampo === true;

  const descricao = [temAjuda ? idAjuda : null, erroNoCampo ? idErro : null]
    .filter((id): id is string => id !== null)
    .join(' ');

  return (
    <form
      className="pergunta"
      onSubmit={(evento) => {
        evento.preventDefault();
        if (enviando) return;
        // A conversão para número acontece no envio, e não a cada tecla: em
        // pt-BR se digita "3," antes de "3,5", e converter no meio apagaria a
        // vírgula na frente de quem a digitou.
        aoEnviar(no.subtipo === 'numero' ? numeroOuTexto(String(rascunho ?? '')) : rascunho);
      }}
    >
      <div className="campo campo--convite">
        <h2 className="pergunta__rotulo" id={idRotulo} ref={titulo} tabIndex={-1}>
          {no.rotulo}
        </h2>

        {no.obrigatorio ? <span className="campo__obrigatorio">Obrigatória</span> : null}

        {temAjuda ? (
          <p className="campo__ajuda" id={idAjuda}>
            {ajuda}
            {ajuda !== '' && faixa ? ' ' : null}
            {faixa}
          </p>
        ) : null}

        <Controle
          no={no}
          valor={rascunho}
          aoMudar={definirRascunho}
          invalido={erroNoCampo}
          idDoRotulo={idRotulo}
          idDaDescricao={descricao === '' ? undefined : descricao}
        />

        {erroNoCampo && problema ? (
          <p className="campo__erro" id={idErro} role="alert">
            {problema.mensagem}
          </p>
        ) : null}
      </div>

      {problema && !problema.noCampo ? (
        <p className="aviso aviso--erro" role="alert">
          <span className="aviso__rotulo">Não enviou</span>
          <span className="aviso__corpo">{problema.mensagem}</span>
        </p>
      ) : null}

      <div className="acoes">
        <button
          className="botao botao--primario"
          type="submit"
          aria-busy={enviando}
          disabled={enviando}
        >
          {rotuloDoBotao(enviando, problema)}
        </button>
      </div>
    </form>
  );
}

export function rotuloDoBotao(enviando: boolean, problema: Problema | null): string {
  if (enviando) return 'Enviando…';
  // Depois de uma falha de rede o botão muda de nome: "Continuar" de novo
  // pareceria que a primeira tentativa foi ignorada.
  if (problema && !problema.noCampo) return 'Tentar de novo';
  return 'Continuar';
}
